import axios from "axios";
import { api } from "../config";

// default
axios.defaults.baseURL = api.API_URL;
// content type
axios.defaults.headers.post["Content-Type"] = "application/json";

function setAuthorization() {
  const authUserStr = localStorage.getItem("authUser");

  // Debug logging
  console.log("🔐 setAuthorization called");
  console.log("📦 authUser from localStorage:", authUserStr);

  if (!authUserStr) {
    console.warn("⚠️ No authUser found in localStorage");
    delete axios.defaults.headers.common["Authorization"];
    return;
  }

  try {
    const authUser = JSON.parse(authUserStr);
    console.log("✅ Parsed authUser:", { hasToken: !!authUser?.token, hasUser: !!authUser?.user });

    if (authUser && authUser.token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${authUser.token}`;
      console.log("✅ Authorization header set:", `Bearer ${authUser.token.substring(0, 20)}...`);
    } else {
      console.warn("⚠️ authUser exists but no token found");
      delete axios.defaults.headers.common["Authorization"];
    }
  } catch (error) {
    console.error("❌ Error parsing authUser from localStorage:", error);
    delete axios.defaults.headers.common["Authorization"];
  }
}


// Set authorization on initial load
setAuthorization();

// Request interceptor - log all outgoing requests
axios.interceptors.request.use(
  function (config) {
    console.log("🚀 Outgoing Request:", {
      url: config.url,
      method: config.method?.toUpperCase(),
      baseURL: config.baseURL,
      fullURL: `${config.baseURL || ''}${config.url}`,
      headers: {
        Authorization: config.headers?.Authorization ?
          `${config.headers.Authorization.substring(0, 30)}...` :
          'NOT SET',
        ContentType: config.headers?.['Content-Type']
      },
      params: config.params,
      data: config.data instanceof FormData ? 'FormData' : config.data
    });
    return config;
  },
  function (error) {
    console.error("❌ Request Error:", error);
    return Promise.reject(error);
  }
);

// intercepting to capture errors
axios.interceptors.response.use(
  function (response) {
    // Backend response structure: { statusCode, status, message, data, ... }
    const responseData = response.data || response;
    
    console.log("✅ Response Received:", {
      url: response.config?.url,
      status: response.status,
      statusText: response.statusText,
      responseStructure: {
        hasStatus: 'status' in responseData,
        statusValue: responseData.status,
        hasStatusCode: 'statusCode' in responseData,
        statusCodeValue: responseData.statusCode,
        hasMessage: 'message' in responseData,
        messageValue: responseData.message,
        hasData: 'data' in responseData,
        dataType: Array.isArray(responseData.data) ? 'array' : typeof responseData.data,
        dataLength: Array.isArray(responseData.data) ? responseData.data.length : 'N/A'
      }
    });
    
    // Check if status is 0 (operation failed but API worked)
    if (responseData.status === 0) {
      console.warn("⚠️ Backend returned status 0:", responseData.message);
      return Promise.reject(new Error(responseData.message || "Operation failed"));
    }
    
    // Return the full response data (includes statusCode, status, message, data)
    return responseData;
  },
  function (error) {
    let message;
    if (error.response) {
      // Backend error response structure: { statusCode, message }
      const errorData = error.response.data || {};
      message = errorData.message || error.message || "An error occurred";

      // Handle specific status codes
      switch (error.response.status) {
        case 500:
          message = errorData.message || "Internal Server Error";
          break;
        case 401:
          message = errorData.message || "Invalid credentials";
          break;
        case 404:
          message = errorData.message || "Sorry! the data you are looking for could not be found";
          break;
        case 400:
          message = errorData.message || "Bad request";
          break;
        default:
          message = errorData.message || error.message || "An error occurred";
          console.error("API Error Details:", {
            status: error.response.status,
            statusText: error.response.statusText,
            data: error.response.data,
            url: error.config?.url,
            method: error.config?.method
          });
      }
      return Promise.reject(new Error(message));
    } else if (error.request) {
      // The request was made but no response was received
      message = "No response received from server";
      return Promise.reject(new Error(message));
    } else {
      // Something happened in setting up the request that triggered an Error
      return Promise.reject(new Error(error.message || "An error occurred"));
    }
  }
);


class APIClient {
  /**
   * Fetches data from given url
   */
  get(url, params) {
    setAuthorization(); // Ensure authorization is set before each request

    let response;
    let paramKeys = [];

    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== "") {
          let filterValue = params[key].toString();
          filterValue = filterValue.replace("e=>e.", "");
          paramKeys.push(key + "=" + filterValue);
        }
      });

      const queryString = paramKeys.length ? paramKeys.join("&") : "";
      response = axios.get(`${url}?${queryString}`);
    } else {
      response = axios.get(url);
    }

    return response;
  }
  /**
   * post given data to url
   */
  create(url, data) {
    setAuthorization();

    // If data is FormData, don't set Content-Type header (let browser set it with boundary)
    if (data instanceof FormData) {
      return axios.post(url, data, {
        headers: {
          'Content-Type': undefined // Let browser set the correct multipart/form-data header
        }
      });
    }

    return axios.post(url, data);
  }
  /**
   * Updates data
   */
  update(url, data) {
    setAuthorization();
    return axios.patch(url, data);
  }

  put(url, data) {
    setAuthorization();
    if (data instanceof FormData) {
      return axios.put(url, data, {
        headers: {
          'Content-Type': undefined // Let browser set the correct multipart/form-data header
        }
      });
    }
    return axios.put(url, { ...data });
  }
  /**
   * Delete
   */
  delete(url, config) {
    setAuthorization();
    if (config && typeof config === 'object') {
      return axios.delete(url, { ...config });
    }
    return axios.delete(url);
  }
}

const getLoggedinUser = () => {
  const user = localStorage.getItem("authUser");
  if (!user) {
    return null;
  }
  return JSON.parse(user);
};

export { APIClient, setAuthorization, getLoggedinUser };
